import React, { useEffect, useRef, forwardRef } from 'react'
import { useTranslation } from 'react-i18next'
import tinycolor from 'tinycolor2'
import 'canvas-datagrid'
import { useTheme } from '@mui/material'
import validators from '../../../../../../utils/validators'

function hasError(value, row, column) {
  if (
    (typeof value === 'undefined' && column === 0 && row > -1) ||
    (value === '' && column === 0 && row > -1)
  ) {
    return true
  } else if (!validators.isValidNItem(value) && column === 0 && row > -1) {
    return true
  }
  if (
    (typeof value === 'undefined' && column === 1 && row > -1) ||
    (value === '' && column === 1 && row > -1)
  ) {
    return true
  }
  if (
    (typeof value === 'undefined' && column === 2 && row > -1) ||
    (value === '' && column === 2 && row > -1)
  ) {
    return true
  }
  return false
}

const CanvasDatagrid = forwardRef((props, parentRef) => {
  const gridRef = useRef(null)

  const theme = useTheme()
  const { t } = useTranslation()

  const { corrections = {}, onRevertCorrection } = props

  // Refs to keep event handlers in sync with latest prop values
  const correctionsRef = useRef(corrections)
  const onRevertCorrectionRef = useRef(onRevertCorrection)

  useEffect(() => {
    correctionsRef.current = corrections
    onRevertCorrectionRef.current = onRevertCorrection
  }, [corrections, onRevertCorrection])

  useEffect(() => {
    gridRef.current.data = props.data
  }, [props.data])

  // Re-render cells when corrections change
  useEffect(() => {
    if (gridRef.current) {
      gridRef.current.draw()
    }
  }, [corrections])

  useEffect(() => {
    gridRef.current.removeEventListener('endedit')
    gridRef.current.addEventListener('endedit', (e) => {
      const newValueHasError = hasError(
        e.value,
        e.cell.rowIndex,
        e.cell.columnIndex
      )
      const oldValueHasError = hasError(
        e.cell.value,
        e.cell.rowIndex,
        e.cell.columnIndex
      )

      // Clear correction when user edits the cell
      const cellKey = `${e.cell.rowIndex}-${e.cell.columnIndex}`
      if (correctionsRef.current[cellKey]) {
        // Correction acknowledged via manual edit — remove from map via onEditSuccess
      }

      if (newValueHasError && !oldValueHasError) {
        props.onEditFailure(e.cell.rowIndex, e.cell.columnIndex)
      } else if (!newValueHasError && oldValueHasError) {
        props.onEditSuccess(e.cell.rowIndex, e.cell.columnIndex)
      } else {
        // Trigger edit success to clear correction highlight even if no error change
        props.onEditSuccess(e.cell.rowIndex, e.cell.columnIndex)
      }
    })

    gridRef.current.removeEventListener('contextmenu')
    gridRef.current.addEventListener('contextmenu', (e) => {
      if (!e.cell || e.cell.rowIndex === undefined) return
      e.items.splice(0, e.items.length)

      // Offer revert option for suggestion corrections
      const cellKey = `${e.cell.rowIndex}-${e.cell.columnIndex}`
      const correction = correctionsRef.current[cellKey]
      if (correction?.type === 'suggestion') {
        e.items.push({
          title: t(
            'create-chassis-dialog.bulk-upload-wizard.context-menu-revert-correction'
          ),
          click() {
            if (onRevertCorrectionRef.current) {
              onRevertCorrectionRef.current(
                e.cell.rowIndex,
                e.cell.columnIndex,
                correction.originalValue
              )
            }
          },
        })
      }

      const hasAddOption = e.items.some(
        (item) =>
          item.title ===
          t(
            'create-vehicle-type-dialog.bulk-upload-wizard.preview-menu-add-row'
          )
      )
      if (!hasAddOption) {
        e.items.push({
          title: t(
            'create-vehicle-type-dialog.bulk-upload-wizard.preview-menu-add-row'
          ),
          click() {
            gridRef.current.addRow({})
            props.onAddRow(props.data.length - 1)
          },
        })
      }

      const hasDeleteOption = e.items.some(
        (item) =>
          item.title ===
          t(
            'create-vehicle-type-dialog.bulk-upload-wizard.preview-menu-delete-row'
          )
      )
      if (!hasDeleteOption) {
        e.items.push({
          title: t(
            'create-vehicle-type-dialog.bulk-upload-wizard.preview-menu-delete-row'
          ),
          click() {
            gridRef.current.deleteRow(e.cell.rowIndex)
            props.onDeleteRow(e.cell.rowIndex)
          },
        })
      }
    })

    gridRef.current.removeEventListener('rendercell')
    gridRef.current.addEventListener('rendercell', (e) => {
      const cellKey = `${e.cell.rowIndex}-${e.cell.columnIndex}`
      const correction = correctionsRef.current[cellKey]
      const cellHasError = hasError(
        e.value,
        e.cell.rowIndex,
        e.cell.columnIndex
      )

      // Errors take priority over suggestion colour
      if (cellHasError) {
        e.ctx.fillStyle = tinycolor(theme.palette.danger.main)
          .lighten(30)
          .toHexString()
      } else if (correction?.type === 'suggestion') {
        e.ctx.fillStyle = '#FFF9C4' // light yellow
      } else if (e.cell.rowIndex !== -1 && e.cell.columnIndex !== -1) {
        e.ctx.fillStyle = theme.palette.secondary.contrastText
      }
    })

    // Tooltip on hover
    gridRef.current.removeEventListener('mousemove')
    gridRef.current.addEventListener('mousemove', (e) => {
      const cellKey = `${e.cell.rowIndex}-${e.cell.columnIndex}`
      const correction = correctionsRef.current[cellKey]

      if (correction?.type === 'suggestion') {
        gridRef.current.canvas.title = t(
          'create-chassis-dialog.bulk-upload-wizard.cell-tooltip-suggestion',
          { corrected: correction.correctedValue }
        )
      } else {
        gridRef.current.canvas.title = ''
      }
    })
  }, [])

  // On unmount
  useEffect(() => {
    const ref = gridRef.current
    return () => {
      if (ref) {
        ref.removeEventListener('endedit')
        ref.removeEventListener('rendercell')
        ref.removeEventListener('contextmenu')
        ref.removeEventListener('mousemove')
        if (ref.input) {
          ref.input.remove()
        }
        ref.dispose()
      }
    }
  }, [])

  return (
    <canvas-datagrid
      style={{
        width: '100%',
        height: '100%',
        editCellBackgroundColor: theme.palette.secondary.contrastText,
        gridBackgroundColor: theme.palette.secondary.contrastText,
        rowHeaderCellBackgroundColor: theme.palette.secondary.contrastText,
        columnHeaderCellBackgroundColor: theme.palette.secondary.contrastText,
        cellBorderColor: theme.palette.captGrey.light,
        columnHeaderCellBorderColor: theme.palette.captGrey.light,
        columnHeaderCellCapBorderColor: theme.palette.captGrey.light,
        columnHeaderCellCapBackgroundColor:
          theme.palette.secondary.contrastText,
        columnHeaderCellHoverBackgroundColor: theme.palette.background.light,
        rowHeaderCellHoverBackgroundColor: theme.palette.background.light,
        rowHeaderCellBorderColor: theme.palette.captGrey.light,
        rowHeaderCellSelectedColor: theme.palette.secondary.main,
        activeRowHeaderCellBackgroundColor: theme.palette.primary.background,
        activeColumnHeaderCellBackgroundColor: theme.palette.primary.background,
        activeHeaderCellBackgroundColor: theme.palette.primary.background,
        columnHeaderOrderByArrowColor: theme.palette.primary.main,
        activeCellOverlayBorderColor: theme.palette.primary.main,
        activeCellBorderColor: theme.palette.primary.main,
        activeCellSelectedBackgroundColor: theme.palette.background.light,
        cornerCellBackgroundColor: theme.palette.background.light,
        contextMenuBackground: theme.palette.background.light,
        contextMenuHoverColor: theme.palette.primary.main,
        contextMenuHoverBackground: theme.palette.primary.background,
        contextMenuArrowColor: theme.palette.primary.main,
        cellWhiteSpace: 'normal',
      }}
      ref={(ref) => {
        gridRef.current = ref
        if (parentRef) {
          parentRef.current = ref
        }
      }}
      editable
      multiLine
      autoResizeRows
      allowRowResizeFromCell
      allowColumnResizeFromCell
    />
  )
})

CanvasDatagrid.displayName = 'CanvasDatagrid'
export default CanvasDatagrid
